import { useEffect } from 'react';
import { createState, useState } from '@hookstate/core';
import { Persistence } from '@hookstate/persistence';
import { useMutation } from '@tanstack/react-query';

import useAuth from './useAuth';
import useOffersData from 'features/SpilKlub/state/useOffersData';

import {
    required,
    maxLength,
    validate,
    number,
    minValue,
    maxValue,
    regexp,
} from '../../../utils/Validation';
import { stateToEntries, stateToObject } from '../../../utils/HookStatePlugins';
import { createGroupWithPool } from '../server/requests';
import { serializeGroup } from '../server/serializers';
import useResponseHandler from './useResponseHandler';

import t from '../texts';

const STORAGE_KEY = 'SK.createGroup';

const initialState = {
    name: '',
    image: '',
    deadline: 15,
    sharePrice: 50,
    shareNumber: 1,
    maxShares: 100,
    isPublic: false,
};

const formState = createState(initialState);

const validators = {
    name: [
        required({ message: t.validation.required }),
        maxLength({ maxLength: 30 }),
        regexp({ regex: /^ +$/, message: t.validation.clubNameOnlySpaces }),
    ],
    deadline: required(),
    sharePrice: [
        required({ message: t.validation.required }),
        number({ message: t.validation.onlyNumbers }),
        minValue({ minValue: 50, message: 'Minimum 50 kr.' }),
        maxValue({ maxValue: 1000, message: 'Maximum 1000 kr.' }),
    ],
    maxShares: [
        required(),
        number({ message: t.validation.onlyNumbers }),
        minValue({ minValue: 2, message: 'Min. 2 andele' }),
    ],
    shareNumber: [
        required({ message: t.validation.required }),
        number({ message: t.validation.onlyNumbers }),
    ],
};

const useGroupForm = ({
    defaults,
    onSuccess,
    groupId = null,
    onError,
    selections,
    combinationPrice,
    combinationCount,
}) => {
    const form = useState(formState);
    form.attach(Persistence(STORAGE_KEY));

    const { extId } = useOffersData();

    useEffect(
        () => {
            if (defaults) {
                form.merge(defaults);
            } else {
                form.merge(initialState);
            }
        },
        [defaults]
    );

    const values = stateToObject(form);

    const { userId } = useAuth();

    // validation
    const errors = useState({});

    useEffect(
        () => {
            errors.set(validate(stateToObject(form), validators));
        },
        [
            form.name.value,
            form.deadline.value,
            form.sharePrice.value,
            form.maxShares.value,
            form.shareNumber.value,
        ]
    );

    const hasErrors = () => {
        return stateToEntries(errors).some(([, error]) => Boolean(error));
    };

    // persistence

    const { handleResponse } = useResponseHandler({
        requestHandler: createGroupWithPool,
        payload: serializeGroup(
            { ...values, groupId },
            userId,
            extId,
            selections,
            combinationPrice,
            combinationCount
        ),
    });

    const mutationFn = async () => {
        let response;

        try {
            response = await handleResponse();

            if (onSuccess) {
                onSuccess(response);
            }
            return response;
        } catch (e) {
            onError(e);
        }
    };

    const mutation = useMutation({
        mutationFn,
        enabled: !hasErrors(),
    });

    return {
        setter: (value) => form.merge(value),
        errors,
        values: form,
        hasErrors: hasErrors(),
        save: mutation.mutateAsync,
        status: mutation,
    };
};

export default useGroupForm;
